/**
 * Icône d'information à côté d'un libellé, révélant une explication courte au survol, au focus
 * clavier ou au clic (`docs/DECISIONS.md` D77).
 *
 * Aucune librairie de tooltip/popover n'est installée dans ce projet : ce composant est
 * volontairement sans dépendance, avec la révélation faite en CSS (`:hover`/`:focus-within`) pour
 * souris et clavier. Le clic bascule un état local en plus de ça, seul moyen de fonctionner au
 * tactile, qui ne déclenche jamais `:hover`.
 *
 * Rendu en `<span role="button">`, pas un `<button>` : les cartes du tableau de bord sont elles-mêmes
 * un `<Link>` (`EcranTableauDeBord.tsx`) et un bouton imbriqué dans un lien est invalide en HTML. Le
 * clic et la touche Entrée/Espace arrêtent explicitement la propagation pour ne jamais déclencher la
 * navigation du lien englobant.
 */
import { useEffect, useRef, useState } from "react";

export function InfoBulle({
  texte,
  libelle,
}: {
  texte: string | null | undefined;
  libelle?: string;
}) {
  const [ouvert, definirOuvert] = useState(false);
  const conteneur = useRef<HTMLSpanElement | null>(null);

  useEffect(() => {
    if (!ouvert) return;

    function surClicExterieur(evenement: MouseEvent) {
      if (!conteneur.current?.contains(evenement.target as Node)) {
        definirOuvert(false);
      }
    }
    function surEchap(evenement: KeyboardEvent) {
      if (evenement.key === "Escape") definirOuvert(false);
    }

    document.addEventListener("click", surClicExterieur);
    document.addEventListener("keydown", surEchap);
    return () => {
      document.removeEventListener("click", surClicExterieur);
      document.removeEventListener("keydown", surEchap);
    };
  }, [ouvert]);

  if (!texte) return null;

  function basculer(evenement: { preventDefault: () => void; stopPropagation: () => void }) {
    evenement.preventDefault();
    evenement.stopPropagation();
    definirOuvert((valeur) => !valeur);
  }

  return (
    <span
      ref={conteneur}
      className={ouvert ? "info-bulle info-bulle--ouverte" : "info-bulle"}
    >
      <span
        role="button"
        tabIndex={0}
        aria-expanded={ouvert}
        aria-label={`Explication : ${libelle ?? "indicateur"}`}
        className="info-bulle__declencheur"
        onClick={basculer}
        onKeyDown={(evenement) => {
          if (evenement.key === "Enter" || evenement.key === " ") basculer(evenement);
        }}
      >
        i
      </span>
      <span role="tooltip" className="info-bulle__contenu">
        {texte}
      </span>
    </span>
  );
}
